/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component, Fragment } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'

import {
  EuiBadge,
  EuiButton,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiLoadingSpinner,
  EuiPopoverFooter,
  EuiSpacer,
  EuiText,
} from '@elastic/eui'

import { CuiAlert } from '@modules/cui/Alert'
import history from '@modules/utils/history'
import Header from '@modules/cui/Header'
import PermissionsGate from '@modules/permissions-components/PermissionsGate'
import CuiToggleablePopoverForClassComp from '@modules/cui/CuiToggleablePopoverForClassComp'

import { topologyInstanceConfigurationCrumbs } from '@/lib/crumbBuilder'
import { topologyEditInstanceConfigurationUrl } from '@/lib/urlBuilder'

import DangerButton from '../../../DangerButton'
import DeprecationWarning from '../DeprecationWarning'

import ConfigurationSummary from './components/ConfigurationSummary'
import Allocators from './components/Allocators'
import InstanceTypes from './components/InstanceTypes'
import Multipliers from './components/Multipliers'

import type { AllProps as Props } from './types'

type State = {
  icVersion: number
}

class InstanceConfigurationView extends Component<Props> {
  state: State = {
    icVersion: this.props.instanceConfiguration?.config_version ?? 0,
  }

  componentDidMount() {
    const { fetchInstanceConfiguration } = this.props
    fetchInstanceConfiguration()
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (prevProps.instanceConfiguration == null && this.props.instanceConfiguration) {
      // we have received the instanceConfiguration
      if (this.props.instanceConfiguration.config_version != null) {
        // config_version is actually set
        this.setState({ icVersion: this.props.instanceConfiguration.config_version })
      }
    }
  }

  render() {
    return (
      <Fragment>
        {this.renderTitle()}

        {this.renderContent()}
      </Fragment>
    )
  }

  renderTitle() {
    const { regionId, instanceId, instanceConfiguration, fetchInstanceConfiguration } = this.props

    if (!instanceConfiguration) {
      return (
        <Header
          breadcrumbs={topologyInstanceConfigurationCrumbs({ regionId, instanceId })}
          name={
            <FormattedMessage
              id='instance-configuration-view.title-placeholder'
              defaultMessage='Loading instance configuration...'
            />
          }
        />
      )
    }

    const { name, system_owned } = instanceConfiguration

    return (
      <Header
        breadcrumbs={topologyInstanceConfigurationCrumbs({ regionId, instanceId })}
        name={
          <EuiFlexGroup gutterSize='m' alignItems='center'>
            <EuiFlexItem grow={false}>
              <div>{name}</div>
            </EuiFlexItem>

            {instanceConfiguration.config_version != null && (
              <EuiFlexItem>
                <CuiToggleablePopoverForClassComp
                  toggleButton={(togglePopoverFunc) => (
                    <EuiButton onClick={togglePopoverFunc} size='s'>
                      <FormattedMessage
                        id='instance-configuration-summary.ic-config-version'
                        defaultMessage='Version'
                      />
                    </EuiButton>
                  )}
                >
                  <Fragment>
                    <EuiFormRow
                      label={
                        <FormattedMessage
                          id='instance-configuration-summary.ic-config-version-explanation'
                          defaultMessage='ICs are versioned and you can type in a value here to get a previous version. Current version:'
                        />
                      }
                      helpText={
                        <FormattedMessage
                          id='instance-configuration-summary.ic-config-version-help-text'
                          defaultMessage='Versions are integer numbers starting at 0'
                        />
                      }
                    >
                      <EuiFieldText
                        value={this.state.icVersion}
                        onChange={(e) => this.setState({ icVersion: e.target.value })}
                      />
                    </EuiFormRow>
                    <EuiPopoverFooter>
                      <EuiButton onClick={() => fetchInstanceConfiguration(this.state.icVersion)}>
                        <FormattedMessage
                          id='instance-configuration-summary.ic-config-version-submit'
                          defaultMessage='Fetch version'
                        />
                      </EuiButton>
                    </EuiPopoverFooter>
                  </Fragment>
                </CuiToggleablePopoverForClassComp>
              </EuiFlexItem>
            )}

            {system_owned && (
              <EuiFlexItem grow={false}>
                <EuiBadge color='primary'>
                  <FormattedMessage
                    id='instance-configuration-summary.system-owned-label'
                    defaultMessage='System owned'
                  />
                </EuiBadge>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        }
      >
        {this.renderHeaderActions()}
        <EuiSpacer size='s' />
      </Header>
    )
  }

  renderHeaderActions() {
    const {
      regionId,
      instanceId,
      instanceConfiguration,
      deleteInstanceConfiguration,
      deleteInstanceConfigurationRequest,
    } = this.props

    if (!instanceConfiguration) {
      return null
    }

    const systemOwned = instanceConfiguration.system_owned

    const { name } = instanceConfiguration

    return (
      <EuiFlexGroup justifyContent='flexEnd' alignItems='center' gutterSize='s'>
        {!systemOwned && (
          <EuiFlexItem grow={false}>
            <DangerButton
              onConfirm={deleteInstanceConfiguration}
              isEmpty={true}
              modal={{
                confirmButtonText: (
                  <FormattedMessage
                    id='instance-configuration-view.delete-modal.confirm'
                    defaultMessage='Delete instance configuration'
                  />
                ),
                title: (
                  <FormattedMessage
                    id='instance-configuration-view.delete-modal.title'
                    defaultMessage='Delete "{name}"?'
                    values={{
                      name,
                    }}
                  />
                ),
              }}
              isBusy={deleteInstanceConfigurationRequest.inProgress}
            >
              <FormattedMessage
                id='instance-configuration-view.delete-button'
                defaultMessage='Delete'
              />
            </DangerButton>
          </EuiFlexItem>
        )}

        <EuiFlexItem grow={false}>
          <PermissionsGate
            permissions={[
              {
                type: 'instance-configuration',
                action: 'update',
                id: instanceId,
              },
            ]}
          >
            {({ hasPermissions }) => (
              <EuiButton
                disabled={!hasPermissions}
                onClick={() =>
                  history.push(topologyEditInstanceConfigurationUrl(regionId, instanceId))
                }
              >
                <FormattedMessage
                  id='instance-configuration-view.edit-button'
                  defaultMessage='Edit instance configuration'
                />
              </EuiButton>
            )}
          </PermissionsGate>
        </EuiFlexItem>
      </EuiFlexGroup>
    )
  }

  renderContent() {
    const { instanceConfiguration, instanceConfigurationRequest } = this.props

    if (instanceConfigurationRequest.error) {
      return <CuiAlert type='error'>{instanceConfigurationRequest.error}</CuiAlert>
    }

    if (instanceConfigurationRequest.inProgress || instanceConfiguration == null) {
      return (
        <EuiFlexGroup gutterSize='m' alignItems='center'>
          <EuiFlexItem grow={false}>
            <EuiLoadingSpinner size='l' />
          </EuiFlexItem>

          <EuiFlexItem>
            <EuiText>
              <FormattedMessage
                id='instance-configuration-view.loading'
                defaultMessage='Loading...'
              />
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      )
    }

    return this.renderOverview()
  }

  renderOverview() {
    const {
      instanceConfiguration,
      regionId,
      searchAllocatorsSimpleQuery,
      searchAllocatorsRequest,
      searchResults,
    } = this.props

    if (!instanceConfiguration) {
      return null
    }

    const {
      description,
      allocator_filter: clauses,
      storage_multiplier: storageMultiplier,
      discrete_sizes: { resource, sizes, default_size: defaultSize },
      max_zones,
    } = instanceConfiguration

    return (
      <Fragment>
        <DeprecationWarning instanceConfiguration={instanceConfiguration} />
        <ConfigurationSummary description={description} />
        <EuiSpacer />
        <Multipliers
          storageMultiplier={storageMultiplier}
          resource={resource || `memory`}
          sizes={sizes}
          defaultSize={defaultSize || 0}
          maxZones={max_zones}
        />
        <EuiSpacer />
        <InstanceTypes instanceConfiguration={instanceConfiguration} />
        <EuiSpacer />
        <Allocators
          clauses={clauses}
          regionId={regionId}
          searchAllocatorsQuery={searchAllocatorsSimpleQuery}
          searchAllocatorsRequest={searchAllocatorsRequest}
          searchResults={searchResults}
        />
      </Fragment>
    )
  }
}

export default injectIntl(InstanceConfigurationView)
